// A short, focused document for the repair shop -- vehicle details, our
// company details, and the claim reference(s) -- as opposed to the full
// claim PDF (generateClaimPdf.ts), which carries declarations/photos/
// diagrams a repairer doesn't need. Built from the same ClaimData shape
// claimSections.ts already assembles for the on-screen review + full PDF,
// so nothing needs re-fetching.
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ClaimData } from "@/lib/claimSections";
import { formatDate } from "@/lib/dates";

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
}

const dash = (v: string | number | null | undefined) => (v === null || v === undefined || v === "" ? "--" : String(v));

function buildRepairAuthorizationPdfDoc(data: ClaimData): { doc: jsPDF; fileName: string } {
  const { incident, claim, vehicle, broker } = data;
  const doc = new jsPDF();
  let y = 18;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Repair Authorisation", 14, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Generated ${formatDate(new Date())} from Taxis Gold & Black DMS`, 14, y + 6);
  y += 14;

  const addSection = (title: string, rows: [string, string][]) => {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(title, 14, y);
    doc.setFont("helvetica", "normal");
    (doc as any).autoTable({
      body: rows,
      startY: y + 3,
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 1.5 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 55 }, 1: { cellWidth: "auto" } },
      theme: "plain",
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  };

  addSection("Claim Reference", [
    ["Broker Reference", dash(claim.broker_reference)],
    ["Claim Number", dash(claim.claim_number)],
    ["Incident Date", formatDate(incident.incident_date)],
    ["Driver", dash(incident.driver_name)],
  ]);

  addSection("Company Details", [
    ["Company Name", dash(broker.companyName)],
    ["Company Number", dash(broker.companyNumber)],
    ["Address", [broker.address1, broker.address2, broker.city, broker.postalCode].filter(Boolean).join(", ") || "--"],
    ["Contact", dash(broker.contactName)],
    ["Contact Number", dash(broker.contactNumber)],
    ["Contact Email", dash(broker.contactEmail)],
  ]);

  addSection("Vehicle Details", [
    ["Vehicle #", dash(vehicle?.vehicle_number ?? incident.vehicle_number)],
    ["Registration", dash(vehicle?.registration)],
    ["Make / Model", `${dash(vehicle?.make)} ${dash(vehicle?.model)}`.trim()],
    ["Year", dash(vehicle?.year)],
  ]);

  addSection("Damage", [
    ["Damage Description", dash(incident.own_vehicle_damage_description)],
    ["Repair Estimate", incident.repair_estimate != null ? `$${incident.repair_estimate.toFixed(2)}` : "--"],
  ]);

  const fileName = `${sanitizeFilename(incident.driver_name || "claim")}_${incident.incident_date}_Repair_Authorisation.pdf`;
  return { doc, fileName };
}

export function downloadRepairAuthorizationPdf(data: ClaimData): void {
  const { doc, fileName } = buildRepairAuthorizationPdfDoc(data);
  doc.save(fileName);
}

export function getRepairAuthorizationPdfBase64(data: ClaimData): { base64: string; fileName: string } {
  const { doc, fileName } = buildRepairAuthorizationPdfDoc(data);
  const base64 = doc.output("datauristring").split(",")[1] || "";
  return { base64, fileName };
}
